// Playback through the embedded YouTube player, behind the same interface as the audio
// Transport, so every view, shortcut and control works for YouTube songs. YouTube can't shift
// pitch and only plays at fixed speeds; loops are done by seeking back.
import {
  MAX_RATE,
  MIN_RATE,
  type LoopRange,
  type PlayerTransport,
  type TransportEvent,
  type TransportState,
} from '@/audio/transport';
import { PlayerState } from './ytPlayer';

/** Speeds YouTube offers, within the app's range. */
export const YOUTUBE_RATES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].filter(
  (r) => r >= MIN_RATE && r <= MAX_RATE,
);

/** The parts of YouTubePlayer this uses (a fake in tests). */
export interface VideoPlayer {
  state: number;
  onStateChange: (() => void) | null;
  currentTime(): number;
  play(): void;
  pause(): void;
  seek(seconds: number): void;
  setRate(rate: number): void;
  setVolume(volume: number): void;
  setMuted(muted: boolean): void;
}

const LOOP_CHECK_MS = 40;

export const nearestRate = (rate: number): number =>
  YOUTUBE_RATES.reduce((best, r) => (Math.abs(r - rate) < Math.abs(best - rate) ? r : best), 1);

export class YouTubeTransport implements PlayerTransport {
  readonly rateSteps = YOUTUBE_RATES;
  private playing = false;
  private rateValue = 1;
  private loopValue: LoopRange | null = null;
  private volumeValue = 1;
  private mutedValue = false;
  private readonly listeners = new Map<TransportEvent, Set<() => void>>();
  private readonly loopTimer: ReturnType<typeof setInterval>;

  constructor(
    private readonly player: VideoPlayer,
    readonly duration: number,
  ) {
    player.onStateChange = this.onPlayerState;
    this.loopTimer = setInterval(this.checkLoop, LOOP_CHECK_MS);
  }

  get state(): TransportState {
    return this.playing ? 'playing' : 'paused';
  }
  get rate(): number {
    return this.rateValue;
  }
  get loop(): LoopRange | null {
    return this.loopValue;
  }
  get volume(): number {
    return this.volumeValue;
  }
  get muted(): boolean {
    return this.mutedValue;
  }

  /** The video's time (the sync offset doesn't apply: the picture and sound come together). */
  getPosition(): number {
    return Math.max(0, Math.min(this.player.currentTime(), this.duration));
  }

  play(): void {
    if (this.playing) return;
    const loop = this.loopValue;
    if (loop && (this.getPosition() < loop.start || this.getPosition() >= loop.end))
      this.player.seek(loop.start);
    this.player.play();
    this.setPlaying(true);
  }

  pause(): void {
    if (!this.playing) return;
    this.player.pause();
    this.setPlaying(false);
  }

  toggle(): void {
    if (this.playing) this.pause();
    else this.play();
  }

  /** Jumps to `seconds`. While a loop is on, a target outside it goes to the loop start. */
  seek(seconds: number): void {
    let target = Math.max(0, Math.min(seconds, this.duration));
    const loop = this.loopValue;
    if (loop && (target < loop.start || target >= loop.end)) target = loop.start;
    this.player.seek(target);
    this.emit('seek');
    this.emit('change');
  }

  /** The nearest speed YouTube offers. */
  setRate(rate: number): void {
    const next = nearestRate(rate);
    if (next === this.rateValue) return;
    this.rateValue = next;
    this.player.setRate(next);
    this.emit('rate');
    this.emit('change');
  }

  setLoop(loop: LoopRange | null): void {
    if (loop && loop.end > loop.start) {
      const range = { start: Math.max(0, loop.start), end: Math.min(this.duration, loop.end) };
      this.loopValue = range;
      const at = this.getPosition();
      if (at < range.start || at >= range.end) this.player.seek(range.start);
    } else {
      this.loopValue = null;
    }
    this.emit('loop');
    this.emit('change');
  }

  setVolume(volume: number): void {
    this.volumeValue = Math.max(0, Math.min(1, volume));
    this.player.setVolume(Math.round(this.volumeValue * 100));
    this.emit('volume');
  }

  setMuted(muted: boolean): void {
    this.mutedValue = muted;
    this.player.setMuted(muted);
    this.emit('volume');
  }

  on(event: TransportEvent, listener: () => void): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener);
    return () => set.delete(listener);
  }

  dispose(): void {
    clearInterval(this.loopTimer);
    this.player.onStateChange = null;
    this.listeners.clear();
  }

  private setPlaying(playing: boolean): void {
    if (playing === this.playing) return;
    this.playing = playing;
    this.emit('state');
    this.emit('change');
  }

  /** Follows the video's own controls (its play button, the end of the video). */
  private readonly onPlayerState = () => {
    const s = this.player.state;
    if (s === PlayerState.playing) this.setPlaying(true);
    else if (s === PlayerState.paused) this.setPlaying(false);
    else if (s === PlayerState.ended) {
      if (this.loopValue) {
        this.player.seek(this.loopValue.start);
        this.player.play();
        return;
      }
      this.setPlaying(false);
      this.emit('ended');
    }
  };

  private readonly checkLoop = () => {
    const loop = this.loopValue;
    if (!this.playing || !loop) return;
    if (this.player.currentTime() >= loop.end) this.player.seek(loop.start);
  };

  private emit(event: TransportEvent): void {
    for (const listener of this.listeners.get(event) ?? []) listener();
  }
}
